import { useEffect, useState } from "react";
import API, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { renderKbContent } from "./kbMarkdown";

const ROLE_LABEL = { admin: "Admin app", client: "Client portal", vendor: "Supplier portal", in_house: "In-house operatives" };
const bucketFor = (userRole) => (["client", "vendor", "in_house"].includes(userRole) ? userRole : "admin");

/**
 * Create or edit a KB article in a dialog. Used from the Unanswered Questions panel.
 * - mode "create": pre-filled from a gap (question → title, role from the asker); POST /kb/articles
 * - mode "edit":   loads GET /kb/articles/{slug}; PUT /kb/articles/{slug}
 * `gap` (optional) is resolved & linked on save. onSaved(slug) fires afterwards.
 */
export default function KbArticleEditor({ mode = "create", slug, gap, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", summary: "", content: "", role: bucketFor(gap?.kb_role || gap?.user_role), section: "", tags: "", related_pages: "" });
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (mode !== "edit" || !slug) return;
    API.get(`/kb/articles/${slug}`).then(({ data }) => {
      setForm({ title: data.title || "", summary: data.summary || "", content: data.content || "", role: data.role || "admin", section: data.section || "",
        tags: (data.tags || []).join(", "), related_pages: (data.related_pages || []).join(", ") });
    }).catch(() => { toast.error("Couldn't load article"); onClose(); }).finally(() => setLoading(false));
  }, [mode, slug, onClose]);

  useEffect(() => {
    if (mode === "create" && gap && !form.title) {
      const q = (gap.question || "").trim();
      set("title", q ? q.charAt(0).toUpperCase() + q.slice(1).replace(/\?+$/, "") : "");
      set("content", `**The question:** ${q}\n\n**The answer:** \n\n**Step by step:**\n\n1. \n\n2. \n\n**Related:** `);
    }
  }, [mode, gap, form.title]);

  useEffect(() => {
    API.get(`/kb/sections?role=${form.role}`).then(({ data }) => {
      setSections(data.sections || []);
      if (!data.sections?.some(s => s.id === form.section)) set("section", data.sections?.[0]?.id || "");
    }).catch(() => {});
  }, [form.role, form.section]);

  const save = async () => {
    if (form.title.trim().length < 4) return toast.error("Give the guide a title (4+ characters)");
    if (form.content.trim().length < 20) return toast.error("Write the guide content (20+ characters)");
    setSaving(true);
    const body = {
      title: form.title.trim(), summary: form.summary.trim(), content: form.content, role: form.role, section: form.section,
      tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
      related_pages: form.related_pages.split(",").map(t => t.trim()).filter(Boolean),
      gap_id: gap?.id || undefined,
    };
    try {
      const { data } = mode === "edit" ? await API.put(`/kb/articles/${slug}`, body) : await API.post(`/kb/articles`, body);
      toast.success(mode === "edit" ? "Guide updated" : "Guide published");
      onSaved && onSaved(data.slug || slug);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save the guide");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="kb-article-editor">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border sticky top-0 bg-card z-10">
          <div>
            <h3 className="text-sm font-semibold font-[Chivo]">{mode === "edit" ? "Fix this guide" : "Add a guide"}</h3>
            {gap && <p className="text-[11px] text-muted-foreground truncate max-w-xl">Answers: "{gap.question}"</p>}
          </div>
          <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground" data-testid="kb-article-editor-close">Close</button>
        </div>
        {loading ? <p className="text-center text-sm text-muted-foreground py-10">Loading…</p> : (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-3">
                <Label className="text-xs">Title</Label>
                <Input value={form.title} onChange={e => set("title", e.target.value)} className="mt-1" data-testid="kb-editor-title" />
              </div>
              <div>
                <Label className="text-xs">Who is it for</Label>
                <Select value={form.role} onValueChange={v => set("role", v)} disabled={mode === "edit"}>
                  <SelectTrigger className="mt-1" disabled={mode === "edit"} aria-disabled={mode === "edit"} data-testid="kb-editor-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ROLE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Section</Label>
                <Select value={form.section} onValueChange={v => set("section", v)}>
                  <SelectTrigger className="mt-1" data-testid="kb-editor-section"><SelectValue placeholder="Choose a section" /></SelectTrigger>
                  <SelectContent>{sections.map(s => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs">One-line summary</Label>
                <Input value={form.summary} onChange={e => set("summary", e.target.value)} className="mt-1" placeholder="Shown under the title in lists and search" data-testid="kb-editor-summary" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Content <span className="text-muted-foreground font-normal">— **bold** and blank lines for paragraphs</span></Label>
                <button type="button" onClick={() => setPreview(p => !p)} className="text-[11px] text-primary hover:underline" data-testid="kb-editor-preview-toggle">{preview ? "Edit" : "Preview"}</button>
              </div>
              {preview ? (
                <div className="mt-1 border border-border rounded-md p-3 min-h-[240px] space-y-1" data-testid="kb-editor-preview">{renderKbContent(form.content)}</div>
              ) : (
                <textarea value={form.content} onChange={e => set("content", e.target.value)} rows={14}
                  className="mt-1 w-full text-sm rounded-md border border-border bg-background px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-primary" data-testid="kb-editor-content" />
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Search tags <span className="text-muted-foreground font-normal">(comma separated)</span></Label>
                <Input value={form.tags} onChange={e => set("tags", e.target.value)} className="mt-1" placeholder="invoice, po number" data-testid="kb-editor-tags" />
              </div>
              <div>
                <Label className="text-xs">Show on pages <span className="text-muted-foreground font-normal">(routes, comma separated)</span></Label>
                <Input value={form.related_pages} onChange={e => set("related_pages", e.target.value)} className="mt-1" placeholder="portal/invoices, jobs" data-testid="kb-editor-pages" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
              <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
              <Button size="sm" onClick={save} disabled={saving} data-testid="kb-editor-save">
                {saving ? "Saving…" : mode === "edit" ? "Save & resolve" : "Publish & resolve"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
